import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError } from '../../core/_axios';
import { Agency } from '../../core/_models';
import { adminGetAgencies } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import {
    ColumnDef,
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender
} from '@tanstack/react-table';
import { useAgencies } from '../../hooks/useAgencies';
import { usePageHeader } from '../../contexts/PageHeaderContext';
export function AgenciesList() {
    usePageTitle('View Agencies');
    const { setPageHeader } = usePageHeader();

    const { agencies, totalAgencies, loading, loadingError, reload: loadAgencies } = useAgencies();
    
    // Set up page header
    useEffect(() => {
        setPageHeader('View Agencies');
        return () => setPageHeader(null);
    }, [setPageHeader]);

    const data = useMemo(() => agencies, [agencies]);
    const columns = useMemo<ColumnDef<Agency>[]>(
        () => [
            {
                accessorKey: 'name',
                header: 'Name',
                cell: ({ getValue, row }) => (
                    <Link
                        className="text-blue-600 hover:underline"
                        to={`/admin/agencies/${row.original.unique_identifier}`}
                    >
                        {getValue() as string}
                    </Link>
                ),
            },
            /*
            {
                accessorKey: 'last_service_at',
                header: 'Agency Item',
                cell: ({ getValue }) => DateTime.fromISO(getValue() as string).toRelative(),
            },
            {
                accessorKey: 'last_signature_at',
                header: 'Another',
                cell: ({ getValue }) => DateTime.fromISO(getValue() as string).toRelative(),
            },
            */
            {
                accessorKey: 'inserted_at',
                header: 'Created',
                cell: ({ getValue }) => DateTime.fromISO(getValue() as string).toRelative(),
            },
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadAgencies}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fixed">
            <div className="flex justify-end pb-4">
                <Link className="btn btn-sm btn-primary" to="/admin/add-agency">
                    <i className="ki-filled ki-plus-squared text-sm me-1"></i>
                    Add Agency
                </Link>
            </div>
            <div className="overflow-x-auto">
                <table className="table table-fixed table-border">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th key={header.id} className="px-4 py-2 bg-gray-100 cursor-pointer" onClick={header.column.getToggleSortingHandler()}>
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        <span>
                                            {header.column.getIsSorted() === 'asc' ? ' 🔼' : header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length > 0 ? (
                            table.getRowModel().rows.map(row => (
                                <tr key={row.id} className="border-t">
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} className="px-4 py-2">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-4">
                                    No agencies have been created.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
